import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  FollowUpWorkflowStep,
  getFollowUpAdvanceAmountPreview,
  getFollowUpDiscussionDetails,
  getFollowUpProgress,
  submitFollowUpInvoiceAdvanceAmount,
} from "../../api/followUpDiscussion";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

/**
 * Splits the backend-rendered preview message into a header line and a list of
 * `Label : Value` rows so the UI can align them side-by-side. Falls back to
 * plain text rendering if a line doesn't match the `Label : Value` shape.
 */
function parsePreviewMessage(message: string): {
  header: string;
  rows: Array<{ label: string; value: string } | { text: string }>;
} {
  const lines = message.split("\n");
  if (lines.length === 0) return { header: "", rows: [] };
  const header = lines[0] ?? "";
  const rows: Array<{ label: string; value: string } | { text: string }> = [];
  for (const raw of lines.slice(1)) {
    const line = raw.trim();
    if (!line) continue;
    const idx = line.indexOf(":");
    if (idx > 0) {
      rows.push({
        label: line.slice(0, idx).trim(),
        value: line.slice(idx + 1).trim(),
      });
    } else {
      rows.push({ text: line });
    }
  }
  return { header, rows };
}

type FollowUpInvoiceAdvanceAmountViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export const FOLLOW_UP_INVOICE_ADVANCE_FORM_ID =
  "follow-up-invoice-advance-form";

export default function FollowUpInvoiceAdvanceAmountView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: FollowUpInvoiceAdvanceAmountViewProps) {
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagePreview, setMessagePreview] = useState("");
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parsedAdvanceAmount = Number.parseFloat(advanceAmount);
  const isAdvanceAmountValid =
    advanceAmount.trim().length > 0 &&
    Number.isFinite(parsedAdvanceAmount) &&
    parsedAdvanceAmount > 0;

  useEffect(() => {
    onSentStatusChange?.(hasSentWhatsapp);
  }, [hasSentWhatsapp, onSentStatusChange]);

  // Load existing lead data + previously saved advance amount.
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFollowUpDiscussionDetails(leadId)
      .then((data) => {
        if (cancelled) return;
        if (data.advanceAmount && data.advanceAmount > 0) {
          setAdvanceAmount(String(data.advanceAmount));
        }
        if (data.preliminary?.phone) {
          setPhoneNumber(data.preliminary.phone);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load lead details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Read step-completion from workflow progress to choose Send vs Save labels.
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getFollowUpProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === FollowUpWorkflowStep.FU_S1_03_InvoiceOfAdvanceAmountToClient
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
        }
      } catch {
        if (!cancelled) setHasSentWhatsapp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Auto-fetch the message preview from backend so the displayed text always matches what
  // will be sent to WhatsApp. Debounced so typing in the amount field doesn't fire a request
  // on every keystroke.
  useEffect(() => {
    if (!leadId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const amountForPreview = isAdvanceAmountValid
      ? parsedAdvanceAmount
      : undefined;
    debounceRef.current = setTimeout(() => {
      setPreviewLoading(true);
      getFollowUpAdvanceAmountPreview(leadId, amountForPreview)
        .then((res) => {
          setMessagePreview(res.message);
        })
        .catch(() => {
          // Preview is non-critical — keep existing value silently on failure.
        })
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [leadId, advanceAmount, isAdvanceAmountValid, parsedAdvanceAmount]);

  const validate = (): boolean => {
    if (!isAdvanceAmountValid) {
      setValidationError("Advance amount must be greater than 0.");
      toast.error("Advance amount must be greater than 0.");
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!validate()) return;
    if (hasSentWhatsapp) {
      // Already sent: silently save without re-sending.
      setSaving(true);
      try {
        await submitFollowUpInvoiceAdvanceAmount(leadId, {
          advanceAmount: parsedAdvanceAmount,
          sendWhatsApp: false,
        });
        toast.success("Invoice advance amount saved successfully.");
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to save invoice advance"
        );
      } finally {
        setSaving(false);
      }
      return;
    }
    setConfirmMode("send");
    setShowConfirmModal(true);
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    if (!validate()) return;
    setShowConfirmModal(false);
    setSaving(true);
    (async () => {
      try {
        await submitFollowUpInvoiceAdvanceAmount(leadId, {
          advanceAmount: parsedAdvanceAmount,
          sendWhatsApp: true,
        });
        setHasSentWhatsapp(true);
        toast.success(
          confirmMode === "resend"
            ? "Invoice advance resent to client successfully"
            : "Invoice advance sent to client successfully"
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSaving(false);
      }
    })();
  };

  const handleResend = async () => {
    if (!leadId || saving) return;
    if (!validate()) return;
    setConfirmMode("resend");
    setShowConfirmModal(true);
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_INVOICE_ADVANCE_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Advance Amount input */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label className="text-[12px] font-normal text-[#222] leading-5">
            Advance Amount (Rs.)
          </label>
          <div className="flex h-9 items-stretch overflow-hidden rounded-[6px] border border-[#EEEEEE] bg-white focus-within:ring-1 focus-within:ring-[#222]">
            <input
              type="text"
              inputMode="decimal"
              placeholder="00.00"
              value={advanceAmount}
              onChange={(e) => {
                setAdvanceAmount(e.target.value);
                if (validationError) setValidationError(null);
              }}
              className="flex-1 min-w-0 px-[10px] py-2 text-[12px] leading-5 bg-white focus:outline-none placeholder:text-[#6D6D6D] text-[#222]"
            />
            <Select value="inr" onValueChange={() => {}}>
              <SelectTrigger className="h-full w-[76px] rounded-none rounded-r-[6px] border-0 border-l border-[#EEEEEE] bg-[#F7F7F7] px-[10px] text-[12px] text-[#6D6D6D] focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inr">Rs(₹)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {validationError && (
            <p className="text-[11px] text-[#D92D20]">{validationError}</p>
          )}
        </div>
      </div>

      {/* Message Preview */}
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">Message Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px]">
          {previewLoading && !messagePreview ? (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Loading preview...
            </p>
          ) : messagePreview ? (
            (() => {
              const { header, rows } = parsePreviewMessage(messagePreview);
              return (
                <div
                  className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-2 gap-y-1 font-sans text-[12px] leading-5 text-[#222]"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  {header && (
                    <p className="col-span-3 m-0 mb-2 break-words">
                      {renderWhatsAppFormatting(header)}
                    </p>
                  )}
                  {rows.map((row, i) =>
                    "label" in row ? (
                      <Fragment key={i}>
                        <div className="break-words text-[#222]">
                          {row.label}
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="min-w-0 break-words text-[#222]">
                          {row.value}
                        </div>
                      </Fragment>
                    ) : (
                      <p key={i} className="col-span-3 m-0 break-words">
                        {renderWhatsAppFormatting(row.text)}
                      </p>
                    )
                  )}
                </div>
              );
            })()
          ) : (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Enter an advance amount to preview the message.
            </p>
          )}
        </div>
      </div>

      {/* Phone number + optional Resend button */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSentWhatsapp && (
          <button
            type="button"
            disabled={saving || !isAdvanceAmountValid}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={messagePreview}
        onConfirm={handleConfirmProceed}
        disableConfirm={!isAdvanceAmountValid}
      />
    </form>
  );
}
